import { ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button, Spinner, StarRating } from '../components/design-system';
import {
  fill, followupOpen, missingRequired, toPayload,
  type AnswerEntry, type AnswerMap, type SurveyQuestion,
} from '../lib/tripSurvey';
import { cn } from '../lib/utils';

type Stage = 'loading' | 'invalid' | 'expired' | 'done' | 'form' | 'thanks';

interface SurveyInfo {
  host_first_name: string | null;
  trip_name: string | null;
  completed: boolean;
  expired: boolean;
  questions: SurveyQuestion[];
}

function Question({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="mb-6">
      <div className="text-subhead font-medium text-app-fg">{label}</div>
      {hint && <div className="text-xs text-app-muted mt-0.5">{hint}</div>}
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-[length:calc(22px*var(--text-scale))] text-app-fg mb-5">{title}</h2>
      {children}
    </section>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'min-h-[44px] px-4 rounded-full text-sm font-medium border transition-colors',
        active ? 'bg-app-fg text-app-bg border-app-fg' : 'bg-app-card text-app-fg-2 border-app-border',
      )}
    >
      {children}
    </button>
  );
}

function Choice<T extends string>({ value, options, onChange }: {
  value: T | undefined;
  options: { key: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => <Chip key={o.key} active={value === o.key} onClick={() => onChange(o.key)}>{o.label}</Chip>)}
    </div>
  );
}

function TextArea({ value, onChange, placeholder, rows = 2 }: {
  value?: string; onChange: (v: string) => void; placeholder: string; rows?: number;
}) {
  return (
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value.slice(0, 1000))}
      rows={rows}
      placeholder={placeholder}
      className="w-full rounded-xl border border-app-border bg-app-card px-3.5 py-3 text-subhead text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none resize-none"
    />
  );
}

function QuestionField({ q, entry, vars, onChange }: {
  q: SurveyQuestion;
  entry: AnswerEntry | undefined;
  vars: { host?: string | null; trip?: string | null };
  onChange: (next: AnswerEntry) => void;
}) {
  const set = (v: AnswerEntry['v']) => onChange({ ...entry, v });
  let control: ReactNode = null;

  if (q.kind === 'stars') {
    control = <StarRating value={typeof entry?.v === 'number' ? entry.v : 0} onChange={(n) => set(n)} size={36} />;
  } else if (q.kind === 'scale10') {
    control = (
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 11 }, (_, n) => (
          <button
            key={n}
            type="button"
            onClick={() => set(n)}
            aria-pressed={entry?.v === n}
            aria-label={`${n} out of 10`}
            className={cn(
              'w-11 h-11 rounded-full text-sm font-medium border transition-colors',
              entry?.v === n ? 'bg-app-fg text-app-bg border-app-fg' : 'bg-app-card text-app-fg-2 border-app-border',
            )}
          >
            {n}
          </button>
        ))}
      </div>
    );
  } else if (q.kind === 'choice') {
    control = <Choice value={entry?.v as string | undefined} options={q.options} onChange={(v) => set(v)} />;
  } else if (q.kind === 'yesno') {
    control = (
      <Choice
        value={entry?.v === undefined ? undefined : entry.v ? 'yes' : 'no'}
        options={q.options}
        onChange={(v) => set(v === 'yes')}
      />
    );
  } else if (q.kind === 'multi') {
    const picked = Array.isArray(entry?.v) ? entry.v : [];
    control = (
      <div className="flex flex-wrap gap-2">
        {q.options.map((o) => {
          const on = picked.includes(o.key);
          return (
            <Chip key={o.key} active={on} onClick={() => set(on ? picked.filter((k) => k !== o.key) : [...picked, o.key])}>
              {o.label}
            </Chip>
          );
        })}
      </div>
    );
  } else if (q.kind === 'text') {
    control = <TextArea value={typeof entry?.v === 'string' ? entry.v : ''} onChange={(v) => set(v)} placeholder="Your answer" />;
  }

  return (
    <Question label={fill(q.label, vars)} hint={q.hint ? fill(q.hint, vars) : undefined}>
      {control}
      {followupOpen(q, entry) && (
        <div className="mt-3">
          <TextArea
            value={entry?.note ?? ''}
            onChange={(note) => onChange({ ...entry, note })}
            rows={q.kind === 'yesno' ? 3 : 2}
            placeholder={q.followup?.placeholder ?? 'Tell us more'}
          />
        </div>
      )}
    </Question>
  );
}

/**
 * Post-trip survey, reached from the push or the email at /survey/:token. No
 * login: the token is the key to exactly one survey. Mostly taps; only the
 * two headline ratings are required.
 */
export function Survey() {
  const { token } = useParams<{ token: string }>();
  const [stage, setStage] = useState<Stage>('loading');
  const [info, setInfo] = useState<SurveyInfo | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setStage('invalid'); return; }
    void (async () => {
      const { data, error: err } = await supabase.rpc('get_trip_survey', { p_token: token });
      const row = (data as SurveyInfo[] | null)?.[0];
      if (err || !row) { setStage('invalid'); return; }
      setInfo(row);
      setStage(row.completed ? 'done' : row.expired ? 'expired' : 'form');
    })();
  }, [token]);

  const questions = info?.questions ?? [];
  const vars = { host: info?.host_first_name, trip: info?.trip_name };

  async function submit() {
    const missing = missingRequired(questions, answers);
    if (missing.length) {
      setError(`Please answer: ${missing.map((m) => fill(m, vars).replace(/\?$/, '')).join(' and ')}.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    const { error: err } = await supabase.rpc('submit_trip_survey', { p_token: token, p_answers: toPayload(questions, answers) });
    setSubmitting(false);
    if (err) {
      if (err.code === '23505') { setStage('done'); return; }
      if (err.code === '55000') { setStage('expired'); return; }
      setError('Something went wrong. Please try again.');
      return;
    }
    setStage('thanks');
  }

  const shell = (children: ReactNode) => (
    <div className="min-h-dvh bg-app-bg text-app-fg px-5 py-10 pt-safe-or-10">
      <div className="max-w-[520px] mx-auto">{children}</div>
    </div>
  );

  if (stage === 'loading') return shell(<div className="flex justify-center py-24"><Spinner /></div>);

  if (stage === 'invalid') {
    return shell(
      <div className="text-center py-20">
        <h1 className="font-serif text-3xl mb-2">This link doesn't work</h1>
        <p className="text-sm text-app-muted">It may be incomplete. Open the survey from your email or notification again.</p>
      </div>,
    );
  }

  if (stage === 'expired') {
    return shell(
      <div className="text-center py-20">
        <h1 className="font-serif text-3xl mb-2">This survey has closed</h1>
        <p className="text-sm text-app-muted">The link was open for 7 days, so we can't take new answers. Thank you for travelling with us.</p>
      </div>,
    );
  }

  if (stage === 'done' || stage === 'thanks') {
    return shell(
      <div className="text-center py-20">
        <h1 className="font-serif text-3xl mb-2">{stage === 'thanks' ? 'Thank you' : 'Already answered'}</h1>
        <p className="text-sm text-app-muted">
          {stage === 'thanks'
            ? 'Your answers help us look after hosts and travelers. Safe travels.'
            : 'You have already shared your feedback for this trip. Thank you.'}
        </p>
      </div>,
    );
  }

  const sections: { title: string; questions: SurveyQuestion[] }[] = [];
  for (const q of questions) {
    const last = sections[sections.length - 1];
    if (last && last.title === q.section) last.questions.push(q);
    else sections.push({ title: q.section, questions: [q] });
  }
  const required = questions.filter((q) => q.required).length;

  return shell(
    <>
      <p className="text-xs font-medium text-app-muted mb-2">About 2 minutes</p>
      <h1 className="font-serif text-[length:calc(34px*var(--text-scale))] leading-[1.08] tracking-[-.015em] mb-2">
        How was {fill('{trip}', vars)}?
      </h1>
      <p className="text-sm text-app-muted mb-10">
        Mostly taps.{required > 0 ? ` Only ${required === 1 ? 'one question is' : `${required} questions are`} required.` : ' Everything is optional.'}
      </p>

      {sections.map((sec) => (
        <Section key={sec.title} title={fill(sec.title, vars)}>
          {sec.questions.map((q) => (
            <QuestionField
              key={q.id}
              q={q}
              entry={answers[q.id]}
              vars={vars}
              onChange={(next) => setAnswers((a) => ({ ...a, [q.id]: next }))}
            />
          ))}
        </Section>
      ))}

      {error && <p className="text-sm text-crimson mb-4" role="alert">{error}</p>}
      <Button onClick={() => void submit()} disabled={submitting} className="w-full min-h-[48px]">
        {submitting ? 'Sending…' : 'Send my answers'}
      </Button>
    </>,
  );
}

export default Survey;
